// ---------------------------------------------------------------------------
// LoadPersonaModal -- Load Saved Funnel Personas
// ---------------------------------------------------------------------------

import React, { useState } from 'react';
import { FolderOpen, Trash2, Loader2 } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import type { Persona } from '@/services/v11/personaService';
import { formatDistanceToNow } from 'date-fns';

interface LoadPersonaModalProps {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly onSelect: (persona: Persona) => void;
  readonly personas: readonly Persona[];
  readonly isLoading: boolean;
  readonly onDelete: (id: number) => Promise<void>;
  readonly isDeleting: boolean;
}

export const LoadPersonaModal: React.FC<LoadPersonaModalProps> = ({
  open,
  onClose,
  onSelect,
  personas,
  isLoading,
  onDelete,
  isDeleting,
}) => {
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<number | null>(null);

  const handleDelete = async (id: number, e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent card click

    if (confirmDeleteId === id) {
      // Already confirmed, proceed with delete
      setDeletingId(id);
      try {
        await onDelete(id);
      } finally {
        setDeletingId(null);
        setConfirmDeleteId(null);
      }
    } else {
      // First click, ask for confirmation
      setConfirmDeleteId(id);
      // Reset confirmation after 3 seconds
      setTimeout(() => setConfirmDeleteId(null), 3000);
    }
  };

  const handleSelectPersona = (persona: Persona) => {
    onSelect(persona);
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-v11-text-primary">
            <FolderOpen className="h-5 w-5" />
            Load Persona
          </DialogTitle>
          <DialogDescription className="sr-only">Select a saved persona to load</DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto pr-2">
          {/* Loading State */}
          {isLoading && (
            <div className="space-y-3">
              {[1, 2, 3].map((i) => (
                <div
                  key={i}
                  className="bg-v11-surface border border-v11-border rounded-lg p-4 animate-pulse"
                >
                  <div className="h-5 bg-v11-border rounded w-1/3 mb-2" />
                  <div className="h-4 bg-v11-border rounded w-2/3 mb-3" />
                  <div className="flex gap-2">
                    <div className="h-6 bg-v11-border rounded w-20" />
                    <div className="h-6 bg-v11-border rounded w-24" />
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Empty State */}
          {!isLoading && personas.length === 0 && (
            <div className="text-center py-12">
              <FolderOpen className="h-16 w-16 mx-auto mb-4 text-v11-text-muted opacity-50" />
              <p className="text-v11-text-muted text-sm">
                No saved personas yet.
                <br />
                Build a funnel and save it to get started.
              </p>
            </div>
          )}

          {/* Persona List */}
          {!isLoading && personas.length > 0 && (
            <div className="space-y-3">
              {personas.map((persona) => {
                const isDeleting = deletingId === persona.id;
                const isConfirming = confirmDeleteId === persona.id;
                const percentOfTotal =
                  persona.totalOrigin > 0
                    ? ((persona.finalCount / persona.totalOrigin) * 100).toFixed(1)
                    : '0';

                return (
                  <div
                    key={persona.id}
                    className="bg-v11-surface border border-v11-border rounded-lg p-4 hover:border-indigo-500/50 transition-colors cursor-pointer relative group"
                    onClick={() => !isDeleting && handleSelectPersona(persona)}
                  >
                    {/* Delete Button */}
                    <button
                      onClick={(e) => handleDelete(persona.id, e)}
                      disabled={isDeleting}
                      className="absolute top-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      {isDeleting ? (
                        <Loader2 className="h-4 w-4 text-v11-text-muted animate-spin" />
                      ) : (
                        <Trash2
                          className={`h-4 w-4 ${
                            isConfirming
                              ? 'text-red-500'
                              : 'text-v11-text-muted hover:text-red-500'
                          } transition-colors`}
                        />
                      )}
                    </button>

                    {/* Name */}
                    <h3 className="text-base font-semibold text-v11-text-primary mb-1 pr-8">
                      {persona.name}
                    </h3>

                    {/* Description */}
                    {persona.description && (
                      <p className="text-sm text-v11-text-muted mb-3 line-clamp-2">
                        {persona.description}
                      </p>
                    )}

                    {/* Metadata Badges */}
                    <div className="flex flex-wrap gap-2 items-center">
                      <Badge variant="outline" className="text-xs">
                        {persona.layerCount} {persona.layerCount === 1 ? 'layer' : 'layers'}
                      </Badge>
                      <Badge variant="outline" className="text-xs">
                        {persona.finalCount.toLocaleString()} {persona.finalUnit}
                      </Badge>
                      <Badge variant="outline" className="text-xs">
                        {percentOfTotal}% of total
                      </Badge>
                      <span className="text-xs text-v11-text-muted ml-auto">
                        {formatDistanceToNow(new Date(persona.createdAt), { addSuffix: true })}
                      </span>
                    </div>

                    {/* Confirmation Message */}
                    {isConfirming && (
                      <div className="mt-2 text-xs text-red-500">
                        Click again to confirm deletion
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-v11-border pt-4 mt-4">
          <div className="flex justify-end">
            <Button variant="ghost" onClick={onClose}>
              Close
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
